import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { useSesion } from '@/sesion/useSesion'
import { EstadoCargando } from '@/componentes/Estados'
import { destinoTrasEntrar } from './destino'
import RequiereSesion from './RequiereSesion'
import MunicipioProtegido from './MunicipioProtegido'
import LayoutMunicipio from '@/layout/LayoutMunicipio'
import ElegirMunicipio from '@/paginas/ElegirMunicipio'
import NoEncontrado from '@/paginas/NoEncontrado'

/**
 * El mapa del panel. El municipio activo va en la URL igual que en la API
 * (/m/:municipio/...), asi que cambiar de municipio es cambiar de ruta y un
 * enlace compartido cae siempre en el mismo lugar.
 *
 * Las pantallas del panel se cargan por ruta con lazy(): el login no tiene por
 * que descargar la tabla de la cartera, y cuando entre el mapa de Leaflet
 * (F2-06) ese peso no puede caer sobre la primera pantalla.
 */

// El login tambien va aparte: se lleva react-hook-form y zod, que quien ya
// tiene sesion no necesita para abrir el dashboard.
const Login = lazy(() => import('@/paginas/Login'))
const Inicio = lazy(() => import('@/paginas/Inicio'))
const Cartera = lazy(() => import('@/paginas/Cartera'))
const FichaDeuda = lazy(() => import('@/paginas/FichaDeuda'))
const Contribuyentes = lazy(() => import('@/paginas/Contribuyentes'))
const FichaContribuyente = lazy(() => import('@/paginas/FichaContribuyente'))
// El mapa se lleva Leaflet y su CSS, que son el bulto mas grande del panel.
const Mapa = lazy(() => import('@/paginas/Mapa'))
const Calles = lazy(() => import('@/paginas/Calles'))
const Reportes = lazy(() => import('@/paginas/Reportes'))
const ControlDeEnvios = lazy(() => import('@/paginas/ControlDeEnvios'))
const Bandeja = lazy(() => import('@/paginas/Bandeja'))
const Cargas = lazy(() => import('@/paginas/Cargas'))
const Cartas = lazy(() => import('@/paginas/Cartas'))
const FichaConversacion = lazy(() => import('@/paginas/FichaConversacion'))
const Configuracion = lazy(() => import('@/paginas/Configuracion'))
const Segmentos = lazy(() => import('@/paginas/Segmentos'))
const FichaSegmento = lazy(() => import('@/paginas/FichaSegmento'))
const Plantillas = lazy(() => import('@/paginas/Plantillas'))
const Usuarios = lazy(() => import('@/paginas/Usuarios'))
const Municipios = lazy(() => import('@/paginas/Municipios'))
const FichaPlantilla = lazy(() => import('@/paginas/FichaPlantilla'))
const Campanas = lazy(() => import('@/paginas/Campanas'))
const FichaCampana = lazy(() => import('@/paginas/FichaCampana'))
// Ojo con el nombre: "rutas" aqui son las de TERRENO --el recorrido de un
// gestor-- y no las de este router. Por eso las paginas viven en
// paginas/RutasDeTerreno y paginas/FichaRuta, y sus piezas en src/terreno/.
const RutasDeTerreno = lazy(() => import('@/paginas/RutasDeTerreno'))
const FichaRuta = lazy(() => import('@/paginas/FichaRuta'))
const Convenios = lazy(() => import('@/paginas/Convenios'))
// Las visitas del terreno con su evidencia (F7-17).
const Visitas = lazy(() => import('@/paginas/Visitas'))
const FichaVisita = lazy(() => import('@/paginas/FichaVisita'))
// La consulta publica: la unica pagina que se abre SIN sesion. Va fuera de
// RequiereSesion a proposito, y no usa el cliente con token ni el layout del
// municipio. El porque, en la cabecera del componente.
const ConsultaPublica = lazy(() => import('@/paginas/ConsultaPublica'))
// Lo mismo para quien entro con ClaveUnica: sin sesion del panel, con la suya.
const CuentaDelVecino = lazy(() => import('@/paginas/CuentaDelVecino'))
const FichaConvenio = lazy(() => import('@/paginas/FichaConvenio'))

export default function Rutas() {
  return (
    <Routes>
      <Route path="/login" element={<Cargable elemento={<Login />} />} />
      <Route path="/consulta/:municipio" element={<Cargable elemento={<ConsultaPublica />} />} />
      <Route
        path="/consulta/:municipio/mi-cuenta"
        element={<Cargable elemento={<CuentaDelVecino />} />}
      />

      <Route element={<RequiereSesion />}>
        <Route path="/" element={<Raiz />} />
        <Route path="/municipios" element={<ElegirMunicipio />} />

        <Route path="/m/:municipio" element={<MunicipioProtegido />}>
          <Route element={<LayoutMunicipio />}>
            <Route index element={<Cargable elemento={<Inicio />} />} />
            <Route path="cartera" element={<Cargable elemento={<Cartera />} />} />
            <Route path="cartera/:deuda" element={<Cargable elemento={<FichaDeuda />} />} />
            <Route path="mapa" element={<Cargable elemento={<Mapa />} />} />
            <Route path="calles" element={<Cargable elemento={<Calles />} />} />
            <Route path="contribuyentes" element={<Cargable elemento={<Contribuyentes />} />} />
            <Route
              path="contribuyentes/:contribuyente"
              element={<Cargable elemento={<FichaContribuyente />} />}
            />
            <Route path="segmentos" element={<Cargable elemento={<Segmentos />} />} />
            <Route path="segmentos/nuevo" element={<Cargable elemento={<FichaSegmento />} />} />
            <Route path="segmentos/:segmento" element={<Cargable elemento={<FichaSegmento />} />} />
            <Route path="plantillas" element={<Cargable elemento={<Plantillas />} />} />
            <Route
              path="plantillas/:plantilla"
              element={<Cargable elemento={<FichaPlantilla />} />}
            />
            <Route path="campanas" element={<Cargable elemento={<Campanas />} />} />
            {/* La de crear va ANTES que la de :campana: si no, "nueva" entra
                como id y la ficha pide /campanas/NaN. */}
            <Route path="campanas/nueva" element={<Cargable elemento={<FichaCampana />} />} />
            <Route path="campanas/:campana" element={<Cargable elemento={<FichaCampana />} />} />
            <Route path="rutas" element={<Cargable elemento={<RutasDeTerreno />} />} />
            {/* La de armar va ANTES que la de :ruta: si no, "nueva" entra
                como id y la ficha pide /rutas/NaN. */}
            <Route path="rutas/nueva" element={<Cargable elemento={<FichaRuta />} />} />
            <Route path="rutas/:ruta" element={<Cargable elemento={<FichaRuta />} />} />
            <Route path="cargas" element={<Cargable elemento={<Cargas />} />} />
            <Route path="cartas" element={<Cargable elemento={<Cartas />} />} />
            <Route path="bandeja" element={<Cargable elemento={<Bandeja />} />} />
            <Route
              path="bandeja/:conversacion"
              element={<Cargable elemento={<FichaConversacion />} />}
            />
            <Route path="visitas" element={<Cargable elemento={<Visitas />} />} />
            <Route path="visitas/:visita" element={<Cargable elemento={<FichaVisita />} />} />
            <Route path="convenios" element={<Cargable elemento={<Convenios />} />} />
            {/* La de proponer va ANTES que la de :convenio: si no, "nuevo"
                entra como id y la ficha pide /convenios/NaN. */}
            <Route path="convenios/nuevo" element={<Cargable elemento={<FichaConvenio />} />} />
            <Route path="convenios/:convenio" element={<Cargable elemento={<FichaConvenio />} />} />
            <Route path="reportes" element={<Cargable elemento={<Reportes />} />} />
            <Route path="configuracion" element={<Cargable elemento={<Configuracion />} />} />
            <Route path="usuarios" element={<Cargable elemento={<Usuarios />} />} />
            <Route path="control-envios" element={<Cargable elemento={<ControlDeEnvios />} />} />
            <Route path="municipios" element={<Cargable elemento={<Municipios />} />} />
            <Route path="*" element={<NoEncontrado />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NoEncontrado />} />
    </Routes>
  )
}

/** El envoltorio de una pantalla diferida: mientras baja su chunk, se avisa. */
function Cargable({ elemento }: { elemento: ReactElement }) {
  return (
    <Suspense fallback={<EstadoCargando etiqueta="Cargando la pantalla…" filas={4} />}>
      {elemento}
    </Suspense>
  )
}

/** La raiz manda a donde corresponda segun cuantos municipios tenga el usuario. */
function Raiz() {
  const { sesion } = useSesion()

  return <Navigate to={destinoTrasEntrar(sesion?.municipios ?? [])} replace />
}
